import React, { useState } from 'react';

import {
  Box,
  Button,
  Typography,
  TextField,
  CircularProgress,
  Chip,
  IconButton,
  MenuItem,
  Tab,
  Tabs,
  Tooltip,
} from '@mui/material';
import {
  Delete as DeleteIcon,
  MenuBook as MenuBookIcon,
  Publish as PublishIcon,
} from '@mui/icons-material';
import DeleteConfirmDialog from '../../../shared/components/DeleteConfirmDialog';
import EmptyStateMessage from '../../../shared/components/EmptyStateMessage';
import FormActionBar from '../../../shared/components/FormActionBar';
import InlineAlert from '../../../shared/components/InlineAlert';
import TagEditor from '../../../shared/components/TagEditor';
import { promptTypeLabel } from '../../../shared/constants/promptTypes';
import { usePromptForm } from '../hooks/usePromptForm';
import PromptContentEditor from './PromptContentEditor';
import PromptPreviewPanel from './PromptPreviewPanel';
import PromptSettings from './PromptSettings';

interface EditPromptPanelProps {
  form: ReturnType<typeof usePromptForm>;
}

const EditPromptPanel: React.FC<EditPromptPanelProps> = ({ form }) => {
  const [activeTab, setActiveTab] = useState<'edit' | 'preview'>('edit');
  const [pendingVersionDelete, setPendingVersionDelete] = useState<string | null>(null);

  if (!form.selectedPrompt && !form.isNewPrompt) {
    return (
      <EmptyStateMessage
        icon={MenuBookIcon}
        title="No prompt selected"
        description="Select a prompt from the list or add a new one"
      />
    );
  }

  return (
    <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
      <Box sx={{ px: 3, pt: 2, pb: 1, borderBottom: 1, borderColor: 'divider', display: 'flex', alignItems: 'center', gap: 1 }}>
        <TextField
          value={form.formData.title}
          onChange={(e) => form.handleChange('title', e.target.value)}
          placeholder="Prompt title..."
          variant="standard"
          InputProps={{
            disableUnderline: true,
            sx: { fontSize: '1.25rem', fontWeight: 600 },
          }}
          sx={{ flex: 1 }}
        />
        <Chip
          label={promptTypeLabel(form.formData.type)}
          size="small"
          color="primary"
          variant="outlined"
        />
        <Chip
          label={form.formData.owner === 'global' ? 'Global' : 'Customer'}
          size="small"
          variant="outlined"
        />
        {!form.isNewPrompt && form.selectedPrompt && (() => {
          const publishedVersions = (form.selectedPrompt.availableVersions || []).filter(v => v !== 'DRAFT');
          const hasPublishedVersions = publishedVersions.length > 0;
          if (!hasPublishedVersions) {
            return (
              <Chip label="Unpublished" size="small" color="warning" variant="outlined" />
            );
          }
          // Deletable only while viewing a published version that is not the live one —
          // the live version is what runs, and DRAFT is the working copy.
          const canDeleteViewed =
            form.viewingVersion !== null && form.viewingVersion !== form.selectedPrompt.promptVersion;
          // Deleting re-reads DRAFT, which replaces the form. Blocking on unsaved edits
          // rather than discarding them, the same way Publish is blocked.
          const blockedByUnsavedEdits = form.hasChanges();
          return (
            <>
              <TextField
                select
                size="small"
                value={form.viewingVersion || 'DRAFT'}
                onChange={(e) => form.handleVersionChange(e.target.value)}
                disabled={form.versionLoading || form.deletingVersion !== null}
                sx={{ minWidth: 130 }}
                SelectProps={{ sx: { fontSize: '0.8rem', height: 28 } }}
              >
                <MenuItem value="DRAFT">Draft (editing)</MenuItem>
                {publishedVersions.map(v => (
                  <MenuItem key={v} value={v}>v{v}{v === form.selectedPrompt!.promptVersion ? ' (live)' : ''}</MenuItem>
                ))}
              </TextField>
              {canDeleteViewed && (
                <Tooltip title={blockedByUnsavedEdits
                  ? 'Save or discard your draft edits before deleting a version'
                  : `Delete v${form.viewingVersion} — frees a version slot for publishing`}>
                  <span>
                    <IconButton
                      size="small"
                      color="error"
                      aria-label={`Delete version ${form.viewingVersion}`}
                      onClick={() => setPendingVersionDelete(form.viewingVersion)}
                      disabled={form.deletingVersion !== null || blockedByUnsavedEdits}
                    >
                      {form.deletingVersion === form.viewingVersion
                        ? <CircularProgress size={16} />
                        : <DeleteIcon fontSize="small" />}
                    </IconButton>
                  </span>
                </Tooltip>
              )}
            </>
          );
        })()}
      </Box>

      <DeleteConfirmDialog
        open={pendingVersionDelete !== null}
        title={`Delete version ${pendingVersionDelete}?`}
        onCancel={() => setPendingVersionDelete(null)}
        onConfirm={() => {
          const version = pendingVersionDelete;
          setPendingVersionDelete(null);
          if (version) form.handleDeleteVersion(version);
        }}
        confirmText="Delete Version"
      >
        <Typography variant="body1" sx={{ mb: 2 }}>
          Delete <strong>v{pendingVersionDelete}</strong> of {form.selectedPrompt?.title}?
        </Typography>
        <InlineAlert severity="warning">
          <Typography variant="body2">
            This cannot be undone and that version&apos;s content is lost. Your draft and the
            live version (v{form.selectedPrompt?.promptVersion}) keep working, but anything
            pinned to v{pendingVersionDelete} breaks: an investigation started against it
            fails on its next turn. Pinned references are not checked before deleting.
          </Typography>
        </InlineAlert>
      </DeleteConfirmDialog>

      <Box sx={{ px: 3, py: 1, borderBottom: 1, borderColor: 'divider' }}>
        <TagEditor
          value={form.formData.tags}
          onChange={(tags) => form.handleChange('tags', tags)}
          suggestions={form.allTags}
        />
      </Box>

      <Box sx={{ px: 3, borderBottom: 1, borderColor: 'divider' }}>
        <Tabs
          value={activeTab}
          onChange={(_, value) => setActiveTab(value)}
          sx={{ minHeight: 36 }}
        >
          <Tab value="edit" label="Edit" sx={{ minHeight: 36, py: 0 }} />
          <Tab value="preview" label="Preview" sx={{ minHeight: 36, py: 0 }} />
        </Tabs>
      </Box>

      <Box sx={{ flex: 1, overflow: 'auto', display: 'flex', flexDirection: 'column' }}>
        <Box sx={{ display: activeTab === 'edit' ? 'flex' : 'none', flexDirection: 'column' }}>
          <PromptContentEditor
            content={form.formData.content}
            onChange={(value) => form.handleChange('content', value)}
            contentError={form.fieldErrors.content}
            viewingVersion={form.viewingVersion}
            viewingContent={form.viewingContent}
            versionLoading={form.versionLoading}
            promptType={form.formData.type}
          />

          <PromptSettings
            formData={form.formData}
            isNewPrompt={form.isNewPrompt}
            fieldErrors={form.fieldErrors}
            expanded={form.settingsExpanded}
            onExpandedChange={form.setSettingsExpanded}
            onChange={form.handleChange}
          />
        </Box>
        <Box sx={{ display: activeTab === 'preview' ? 'block' : 'none' }}>
          <PromptPreviewPanel
            key={form.selectedPrompt?.id ?? 'new'}
            content={form.isViewingHistory ? form.viewingContent : form.formData.content}
            customerId={form.customerId}
            promptType={form.formData.type}
          />
        </Box>
      </Box>

      <FormActionBar
        onSave={form.handleSave}
        saving={form.saving}
        saveDisabled={form.publishing || !form.hasChanges()}
        saveLabel={form.isNewPrompt ? 'Create' : 'Save'}
        savingLabel={form.isNewPrompt ? 'Creating...' : 'Saving...'}
        size="small"
        onDelete={!form.isNewPrompt && form.selectedPrompt && !form.isViewingHistory ? form.handleDeleteClick : undefined}
        deleteDisabled={form.saving || form.publishing}
        hideSave={form.isViewingHistory}
        sx={{ px: 3, py: 1.5, mt: 0, pt: 0 }}
      >
        {form.successMessage && (
          <Typography variant="caption" color="success.main">
            {form.successMessage}
          </Typography>
        )}
        {!form.successMessage && !form.isNewPrompt && form.selectedPrompt && !form.isViewingHistory && form.hasChanges() && (
          <Typography variant="caption" color="warning.main">
            Unsaved changes
          </Typography>
        )}
        {!form.isNewPrompt && form.selectedPrompt && !form.isViewingHistory && (
          <Button
            size="small"
            variant="outlined"
            startIcon={form.publishing ? <CircularProgress size={14} /> : <PublishIcon />}
            onClick={form.handlePublish}
            disabled={form.saving || form.publishing || !form.isFormValid() || form.hasChanges() || form.justPublished}
          >
            {form.publishing ? 'Publishing...' : 'Publish'}
          </Button>
        )}
      </FormActionBar>
    </Box>
  );
};

export default EditPromptPanel;
